import React from 'react';
import { useChat } from '../hooks/useChat';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { ErrorBoundary } from './ErrorBoundary';
import { ApiConfig } from '../types/chat';
import { ConversationMessage } from '../utils/dynamodb';

interface ChatContainerProps {
  apiConfig?: Partial<ApiConfig>;
  className?: string;
  showTraces?: boolean;
  maxMessages?: number;
  onConversationTitleChange?: (title: string | null) => void;
  onLoadConversationReady?: (
    loadFn: (
      messages: ConversationMessage[],
      sessionId: string,
      title?: string,
    ) => void,
  ) => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  apiConfig,
  className = '',
  showTraces = true,
  maxMessages = 100,
  onConversationTitleChange,
  onLoadConversationReady,
}) => {
  const {
    messages,
    sessionId,
    isLoading,
    error,
    connectionStatus,
    sendMessage,
    cancelRequest,
    conversationTitle,
    loadConversation,
  } = useChat({
    apiConfig,
    maxMessages,
  });

  // Pass conversation title up to parent component
  React.useEffect(() => {
    if (onConversationTitleChange) {
      onConversationTitleChange(conversationTitle);
    }
  }, [conversationTitle, onConversationTitleChange]);

  // Pass loadConversation function up to parent component
  React.useEffect(() => {
    if (onLoadConversationReady) {
      onLoadConversationReady(loadConversation);
    }
  }, [loadConversation, onLoadConversationReady]);

  return (
    <ErrorBoundary>
      <div
        className={`flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden ${className}`}
      >
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          </div>
        )}

        {/* Messages */}
        <MessageList
          messages={messages}
          isLoading={isLoading}
          showTraces={showTraces}
          autoScroll={true}
          onQuestionClick={sendMessage}
          sessionId={sessionId}
        />

        {/* Input */}
        <ChatInput
          onSendMessage={sendMessage}
          onCancelRequest={cancelRequest}
          isLoading={isLoading}
          disabled={connectionStatus === 'error'}
        />
      </div>
    </ErrorBoundary>
  );
};
